import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import type { Creator } from '@/types';
import {
  formatFollowers,
  rankToFollowerRange,
} from '../utils/creator-formatters';
import { CreatorStatusBadge } from './creator-status-badge';
import { CreatorCategoryBadge } from './creator-category-badge';
import { CreatorVoidWarning } from './creator-void-warning';

function F({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-text-tertiary/60">
        {label}
      </span>
      <div className="mt-0.5 text-sm text-text-primary">{children || '—'}</div>
    </div>
  );
}

function CollectorField({ creator }: { creator: Creator }): ReactNode {
  const navigate = useNavigate();
  const c = creator.expand?.collector;
  if (!c) return null;
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  const photo = c.photo
    ? `/api/files/team_members/${c.id}/${c.photo}`
    : undefined;
  return (
    <F label="Collector">
      <button
        onClick={() => navigate(`/progress/${c.id}`)}
        className="flex items-center gap-xs hover:text-brand-red"
      >
        <Avatar src={photo} name={name || c.ig_username} size="sm" />
        <span>{name}</span>
      </button>
    </F>
  );
}

function PostLink({ url }: { url: string }): ReactNode {
  return (
    <F label="Post">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-red hover:text-brand-coral hover:underline"
      >
        Ver post
      </a>
    </F>
  );
}

function FieldsGrid({ creator }: { creator: Creator }): ReactNode {
  return (
    <div className="grid grid-cols-3 gap-base">
      <F label="Followers">{formatFollowers(creator.followers)}</F>
      <F label="Rango Followers">{rankToFollowerRange(creator.rank)}</F>
      <F label="Categoría">
        <CreatorCategoryBadge
          type={creator.creator_type}
          category={creator.category}
        />
      </F>
      <F label="Status">
        <CreatorStatusBadge status={creator.status} />
      </F>
      <F label="Tipo">{creator.creator_type}</F>
      <F label="Sexo">{creator.sex || '—'}</F>
      <F label="Email">{creator.email}</F>
      <CollectorField creator={creator} />
      {creator.notes && <F label="Notas">{creator.notes}</F>}
      {creator.post_url && <PostLink url={creator.post_url} />}
    </div>
  );
}

export function CreatorFields({ creator }: { creator: Creator }): ReactNode {
  return (
    <div>
      <CreatorVoidWarning creator={creator} />
      <FieldsGrid creator={creator} />
    </div>
  );
}
